import React, { FunctionComponent } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import HRMonitorIcon from '../../../assets/images/home-workouts/hr-monitor.svg';
import InstructorIcon from '../../../assets/images/home-workouts/instructor.svg';
import LeaderBoardOffIcon from '../../../assets/images/home-workouts/leaderboard-off.svg';
import LeaderBoardOnIcon from '../../../assets/images/home-workouts/leaderboard.svg';
import MirrorOffIcon from '../../../assets/images/home-workouts/mirror-off.svg';
import MirrorOnIcon from '../../../assets/images/home-workouts/mirror-on.svg';
import ScreenCastIcon from '../../../assets/images/home-workouts/screen-cast.svg';
import WorkoutSettingsIcon from '../../../assets/images/home-workouts/workout-settings.svg';
import Theme from '../../../theme';
import { defaultScale, fullScale, scaleText } from '../../../utils/scaling';
import { DeviceSetupScreens } from '../../utils/DeviceSetupScreens';
import { disabledOpacity, IconButton } from './IconButton';

interface IProps {
  openSetupScreen: (screen: DeviceSetupScreens) => void;
  mirrorIsActive: boolean;
  isGroupWorkout: boolean;
  liveHeartRate: number | null;
  isLeaderboardActivated: boolean;
  customiseWorkoutSettingsDisabled: boolean;
  enableInstructorChange: boolean;
  enableMirrorSetup: boolean;
  t: (key: string) => string;
}

export const DeviceSetupHeader: FunctionComponent<IProps> = ({
                                                               openSetupScreen,
                                                               mirrorIsActive,
                                                               enableMirrorSetup,
                                                               isLeaderboardActivated,
                                                               isGroupWorkout,
                                                               liveHeartRate,
                                                               customiseWorkoutSettingsDisabled,
                                                               enableInstructorChange,
                                                               t,
                                                             }) => {

  const iconSize = {
    height: defaultScale(20),
    width: defaultScale(20),
  };

  const mirrorIcon = () => {
    if (!enableMirrorSetup) {
      return <MirrorOffIcon color={Theme.WHITE} opacity={disabledOpacity}  {...iconSize} />;
    }
    if (mirrorIsActive) {
      return <MirrorOnIcon color={Theme.WHITE}  {...iconSize} />;
    }
    return <MirrorOffIcon color={Theme.WHITE}  {...iconSize} />;
  };

  const leaderboardIcon = () => {
    if (isLeaderboardActivated) {
      return <LeaderBoardOnIcon color={Theme.WHITE}  {...iconSize} />;
    } else {
      return <LeaderBoardOffIcon color={Theme.WHITE} {...iconSize} />;
    }
  };

  const heartRateIconOrValue = () => {
    if (liveHeartRate) {
      return <Text style={styles.heartRateText}>{liveHeartRate}</Text>;
    }
    return <HRMonitorIcon color={Theme.WHITE}  {...iconSize} />;
  };

  const mirrorLabel = mirrorIsActive ? t('mirrorIsOn') : t('mirrorIsOff');
  const leaderboardLabel = isLeaderboardActivated ? t('leaderboardIsOn') : t('leaderboardIsOff');

  const firstRowButtons = (small = false) => (
    <>
      <IconButton icon={<WorkoutSettingsIcon color={Theme.WHITE} {...iconSize}
                                             opacity={customiseWorkoutSettingsDisabled ? disabledOpacity : 1} />}
                  label={t('workoutSettings')}
                  onPress={() => openSetupScreen(DeviceSetupScreens.MyWorkout)}
                  small={small} disabled={customiseWorkoutSettingsDisabled}
                  opacity={customiseWorkoutSettingsDisabled ? disabledOpacity : 1} />
      <IconButton icon={heartRateIconOrValue()} label={t('heartRateMonitor')}
                  onPress={() => openSetupScreen(DeviceSetupScreens.HeartRate)} small={small} />
      <IconButton icon={<ScreenCastIcon color={Theme.WHITE}  {...iconSize} />} label={t('screenCast')}
                  onPress={() => openSetupScreen(DeviceSetupScreens.ScreenCasting)} small={small} />
    </>
  );

  const secondRowButtons = (small = false) => (
    <>
      <IconButton icon={leaderboardIcon()} label={leaderboardLabel}
                  onPress={() => openSetupScreen(DeviceSetupScreens.LeaderboardSetup)} small={small} />
      <IconButton icon={<InstructorIcon color={Theme.WHITE}
                                        opacity={enableInstructorChange ? 1 : disabledOpacity} {...iconSize} />}
                  label={t('changeInstructor')}
                  onPress={() => openSetupScreen(DeviceSetupScreens.Instructor)} small={small}
                  disabled={!enableInstructorChange} />
      <IconButton icon={mirrorIcon()} label={mirrorLabel}
                  onPress={() => openSetupScreen(DeviceSetupScreens.ScreenMirror)} disabled={!enableMirrorSetup}
                  small={small} />
    </>
  );

  return (
    <View style={styles.container}>
      {
        <>
          <View style={styles.buttonContainer}>
            {firstRowButtons()}
          </View>
          <View style={styles.buttonContainer}>
            {secondRowButtons()}
          </View>
        </>
      }
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: defaultScale(15),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: defaultScale(10),
  },
  heartRateText: {
    ...Theme.textStyle.bodyCopyBold,
    color: Theme.WHITE,
    fontSize: defaultScale(20),
  },
});
